import { useCallback, useEffect, useState } from 'react';
import { UserPlus, X } from 'lucide-react';
import { apiClients, apiPersons } from '../api/client.js';
import Avatar from './Avatar';
import PeoplePicker from './PeoplePicker';

/**
 * #108 — Responsables d'un client, dans la modale d'édition Client (ADMIN).
 *
 * Un responsable peut modifier TOUTES les affaires du client et en changer le
 * project manager. La liste est enregistrée immédiatement (remplacement complet
 * via `PUT /clients/:id/managers`), indépendamment du bouton « Enregistrer » de
 * la modale — même logique que la section Hiérarchie d'une Person.
 */
export default function ClientManagersSection({ clientId, onChange }) {
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPicker, setShowPicker] = useState(false);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { items } = await apiClients.listManagers(clientId);
      setManagers(items ?? []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    if (clientId) reload();
  }, [clientId, reload]);

  const save = async (personIds) => {
    setBusy(true);
    setError(null);
    try {
      const { items } = await apiClients.setManagers(clientId, personIds);
      setManagers(items ?? []);
      onChange?.(items ?? []);
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const fetchOptions = useCallback(
    async (q) => {
      const { items } = await apiPersons.list('active');
      const lower = (q ?? '').toLowerCase().trim();
      return items
        .filter((p) => !managers.some((m) => m.personId === p.id))
        .filter((p) => {
          if (!lower) return true;
          return `${p.firstName} ${p.lastName} ${p.email ?? ''}`.toLowerCase().includes(lower);
        })
        .slice(0, 30)
        .map((p) => ({
          id: p.id,
          fullName: `${p.firstName} ${p.lastName}`,
          firstName: p.firstName,
          lastName: p.lastName,
          jobTitle: p.jobTitle,
          avatarUrl: p.avatarUrl,
        }));
    },
    [managers],
  );

  const onAdd = async (opt) => {
    if (await save([...managers.map((m) => m.personId), opt.id])) setShowPicker(false);
  };

  const onRemove = async (m) => {
    if (!window.confirm(`Retirer ${m.firstName} ${m.lastName} des responsables du client ?`)) return;
    await save(managers.filter((x) => x.personId !== m.personId).map((x) => x.personId));
  };

  return (
    <div style={{ marginTop: 18 }}>
      <div
        style={{
          margin: '4px 0 6px',
          fontSize: 12,
          fontWeight: 600,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          color: 'var(--text-muted)',
        }}
      >
        Responsables du client
      </div>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>
        Ils peuvent modifier toutes les affaires de ce client et en changer le project manager.
      </div>

      {error ? <div style={{ color: 'var(--danger)', fontSize: 12, marginBottom: 8 }}>{error}</div> : null}

      {loading ? (
        <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Chargement…</div>
      ) : managers.length === 0 ? (
        <div style={{ fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic', marginBottom: 8 }}>
          Aucun responsable
        </div>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
          {managers.map((m) => (
            <span
              key={m.personId}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '4px 8px 4px 4px',
                background: 'var(--glass)',
                border: '1px solid var(--glass-border)',
                borderRadius: 'var(--radius-pill)',
                fontSize: 13,
                color: m.archived ? 'var(--text-muted)' : 'var(--text)',
              }}
            >
              <Avatar person={{ firstName: m.firstName, lastName: m.lastName }} size={20} />
              {m.firstName} {m.lastName}
              {m.archived ? <span style={{ fontSize: 11 }}>(archivé)</span> : null}
              <button
                type="button"
                onClick={() => onRemove(m)}
                disabled={busy}
                title="Retirer ce responsable"
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--text-muted)',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: 0,
                }}
              >
                <X size={14} />
              </button>
            </span>
          ))}
        </div>
      )}

      {showPicker ? (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          <div style={{ flex: 1, minWidth: 260 }}>
            <PeoplePicker
              fetchOptions={fetchOptions}
              onSelect={onAdd}
              placeholder="Rechercher une personne…"
              autoFocus
              emptyMessage="Aucune personne disponible"
            />
          </div>
          <button
            type="button"
            onClick={() => setShowPicker(false)}
            title="Annuler"
            style={{
              background: 'transparent',
              border: '1px solid var(--glass-border)',
              borderRadius: 'var(--radius-sm)',
              color: 'var(--text)',
              padding: '6px 10px',
              cursor: 'pointer',
            }}
          >
            Annuler
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setShowPicker(true)}
          disabled={busy || loading}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '6px 12px',
            background: 'transparent',
            color: 'var(--accent)',
            border: '1px dashed var(--glass-border)',
            borderRadius: 'var(--radius-pill)',
            fontSize: 13,
            cursor: 'pointer',
          }}
        >
          <UserPlus size={14} /> Ajouter un responsable
        </button>
      )}
    </div>
  );
}
